import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, InputGroup, Dropdown, Spinner, Alert, Pagination } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { adminService } from '../../services/adminService';
import toast from 'react-hot-toast';
import EditUserModal from './UserEditModal';
import ViewUserModal from './UserViewModal';



const Users: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0); 
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [savingUser, setSavingUser] = useState(false);
  const itemsPerPage = 10;



  useEffect(() => {
    loadUsers();
  }, [currentPage, filterRole, filterStatus]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadUsers();
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminService.getUsers({
        search: searchTerm,
        role: filterRole === 'all' ? undefined : filterRole,
        status: filterStatus === 'all' ? undefined : filterStatus,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: 'created_at',
        sortOrder: 'desc'
      });

      if (response.success && response.data) {
        setUsers(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalUsers(Number(response.pagination.total) || 0); // ADD THIS LINE
        }
      }
    } catch (error: any) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };
  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setShowEditModal(true);
  };
  const handleSaveUser = async (formData: any) => {
    try {
      setSavingUser(true);
      const res = await adminService.adminUpdateUser(editingUser.id, formData);
  
      if (res.success) {
        toast.success('User updated successfully');
        
        // Trigger user status change event if status was modified
        if (formData.status && formData.status !== editingUser.status) {
          const event = new CustomEvent('userStatusChanged', {
            detail: { 
              userId: editingUser.id, 
              newStatus: formData.status.toLowerCase() === 'active' ? 'active' : 'inactive'
            }
          });
          window.dispatchEvent(event);
        }
        
        setShowEditModal(false);
        loadUsers(); // refresh table
      }
    } catch (err) {
      toast.error('Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };
  

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      return;
    }

    try {
      const response = await adminService.deleteUser(userId);
      if (response.success) {
        toast.success('User deleted successfully');
        loadUsers();
      }
    } catch (error: any) {
      toast.error('Failed to delete user');
    }
  };
  
  const filteredUsers = users;

  return (
    <>
      <Helmet>
        <title>Users - MyCinemas Admin Panel</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Users</h2>
            <p className="text-muted mb-0">
              Manage user accounts and permissions
            </p>
          </div>
          {/* <Button variant="primary">
            <i className="bi-plus-circle me-1"></i>
            Add User
          </Button> */}
        </div>

        <Card>
          <Card.Header>
            <Row className="align-items-center">
              <Col>
              <h5 className="mb-0">All Users ({totalUsers})</h5>
              </Col>
              {/* <Col md={2}>
                <Form.Select 
                  value={filterRole} 
                  onChange={(e) => setFilterRole(e.target.value)}
                  disabled={loading}
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                </Form.Select>
              </Col> */}
              <Col md={2}>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  disabled={loading}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  {/* <th>Role</th> */}
                  <th>Status</th>
                  <th>Join Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-5">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      <div className="mt-2 text-muted">Loading users...</div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      <i className="bi-inbox display-4 d-block mb-3"></i>
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div
                            className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{
                              width: "32px",
                              height: "32px",
                              fontSize: "0.875rem",
                            }}
                          >
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="fw-medium">{user.name}</div>
                          </div>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>{user.mobile}</td>
                      {/* <td>
                        <Badge
                          bg={
                            user.role === "Admin"
                              ? "primary"
                              : user.role === "Manager"
                              ? "warning"
                              : "secondary"
                          }
                        >
                          {user.role}
                        </Badge>
                      </td> */}
                      <td>
                        <Badge
                          bg={
                            user.status === "Active"
                              ? "success"
                              : user.status === "Suspended"
                              ? "danger"
                              : "secondary"
                          }
                        >
                          {user.status}
                        </Badge>
                      </td>
                      <td>{user.joindate}</td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant="outline-secondary"
                            size="sm"
                            id={`dropdown-${user.id}`}
                          >
                            <i className="bi-three-dots"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={() => {
                                setSelectedUser(user);
                                setShowViewModal(true);
                              }}
                            >
                              <i className="bi-eye me-2"></i>View Details
                            </Dropdown.Item>

                            <Dropdown.Item onClick={() => handleEditUser(user)}>
                              <i className="bi-pencil me-2"></i>Edit User
                            </Dropdown.Item>

                            <Dropdown.Divider />
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() =>
                                handleDeleteUser(user.id, user.name)
                              }
                            >
                              <i className="bi-trash me-2"></i>Delete User
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
          {totalPages > 1 && (
  <Card.Footer className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 py-3">
    <div className="text-muted small mb-0">
      {totalUsers > 0
        ? `Showing ${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, totalUsers)} of ${totalUsers} users`
        : "No users found"}
    </div>
    <Pagination className="mb-0">
      <Pagination.Prev
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
      />
      {Array.from(
        { length: totalPages },
        (_, index) => index + 1
      ).map((pageNumber) => (
        <Pagination.Item
          key={pageNumber}
          active={pageNumber === currentPage}
          onClick={() => setCurrentPage(pageNumber)}
        >
          {pageNumber}
        </Pagination.Item>
      ))}
      <Pagination.Next
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
      />
    </Pagination>
  </Card.Footer>
)}   
        </Card>
      </div>
      <EditUserModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        user={editingUser}
        onSave={handleSaveUser}
        loading={savingUser}
      />
      <ViewUserModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        user={selectedUser}
      />
    </>
  );
};

export default Users;
